/**
 * TeamContext.js
 * 
 * Zentraler Context für das Multi-Team-System.
 * 
 * SICHERHEIT - OBERSTE PRIORITÄT:
 * - Strikte World-Isolation: Keine Datenvermischung zwischen Accounts
 * - Switch-Blocking: UI wird während World-Wechsel komplett gesperrt
 * - Request-Validation: Jede Aktion validiert die aktuelle activeWorldId
 * - Abort-Controller: Pending Requests werden bei World-Switch abgebrochen
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { auth, firestore, database, functions } from '../firebaseConfig';
import { doc, getDoc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref, get, update, remove } from 'firebase/database';
import { signInWithCustomToken } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { resolveInstanceCap } from '../utils/multiInstanceTiers';

// Verfügbare Features
export const AVAILABLE_FEATURES = {
  leads: 'LeadSwipe Only',
  inbox: 'Smart Inbox & E-Mails',
  offers: 'Angebote & Verträge',
  settings: 'World Settings & AI Settings',
  autoSendButton: 'AI Send+ Button',
  all: 'Vollzugriff'
};

// Default Context Werte
const defaultContextValue = {
  // User IDs
  currentUserId: null,
  activeWorldId: null,
  
  // Account-Typ
  accountType: null,
  memberType: null,
  isOwner: false,
  isMember: false,
  isMasterMember: false,
  isStandardMember: false,
  
  // Multi-Team Support
  availableTeams: [],           // Alle Teams in denen der User ist
  activeTeam: null,             // Das aktive Team-Objekt
  hasMultipleTeams: false,      // Hat User Zugriff auf mehrere Accounts?
  pendingInvitations: [],       // Ausstehende Einladungen
  
  // Team-Infos (nur für Members)
  teamOwnerId: null,
  teamOwnerEmail: null,
  teamOwnerName: null,
  
  // Berechtigungen
  features: [],
  readOnly: false,
  
  // SICHERHEIT: Switch-Status
  isSwitching: false,           // 🔒 UI blocken während Switch
  switchVersion: 0,             // 🔒 Version für Request-Validation
  
  // Status
  isLoading: true,
  isInitialized: false,
  
  // Sub-World System (Multi-Instance per Account)
  parentOwnerUid: null,         // gesetzt, wenn die aktuelle Session eine Sub-World ist
  isSubWorldSession: false,     // Convenience-Flag (= !!parentOwnerUid)
  mySubWorlds: [],              // Sub-Worlds, die der aktuelle Account besitzt (parent only)
  multiInstanceEnabled: false,  // Feature-Flag (Users/{uid}.multiInstance === true)
  multiInstanceCap: 0,          // Max. Anzahl Sub-Worlds (default 10)
  
  // Helper Functions
  hasFeature: () => false,
  canWrite: () => false,
  canAccessSettings: () => false,
  canManageTeam: () => false,
  switchTeam: async () => {},
  validateWorldId: () => false,
  getAbortSignal: () => null,
  acceptInvitation: async () => {},
  declineInvitation: async () => {},
  leaveTeam: async () => false,
  
  // Sub-World Actions
  enterSubWorld: async () => false,
  returnToParentAccount: async () => false,
  createSubWorld: async () => null,
  renameSubWorld: async () => false,
  archiveSubWorld: async () => false,
  transferSubWorld: async () => false,
};

// Context erstellen
const TeamContext = createContext(defaultContextValue);

/**
 * TeamProvider Component
 * Wrapper für die gesamte App
 */
export const TeamProvider = ({ children }) => {
  // User IDs
  const [currentUserId, setCurrentUserId] = useState(null);
  const [activeWorldId, setActiveWorldId] = useState(null);
  
  // Account-Typ
  const [accountType, setAccountType] = useState(null);
  const [memberType, setMemberType] = useState(null);
  
  // Multi-Team Support
  const [availableTeams, setAvailableTeams] = useState([]);
  const [activeTeam, setActiveTeam] = useState(null);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  
  // Team-Infos
  const [teamOwnerId, setTeamOwnerId] = useState(null);
  const [teamOwnerEmail, setTeamOwnerEmail] = useState(null);
  const [teamOwnerName, setTeamOwnerName] = useState(null);
  
  // Berechtigungen
  const [features, setFeatures] = useState([]);
  const [readOnly, setReadOnly] = useState(false);
  
  // SICHERHEIT: Switch-Status
  const [isSwitching, setIsSwitching] = useState(false);
  const [switchVersion, setSwitchVersion] = useState(0);
  const abortControllerRef = useRef(null);
  
  // Sub-World State
  const [parentOwnerUid, setParentOwnerUid] = useState(null);
  const [mySubWorlds, setMySubWorlds] = useState([]);
  const [multiInstanceEnabled, setMultiInstanceEnabled] = useState(false);
  const [multiInstanceCap, setMultiInstanceCap] = useState(0);
  // Sentinel: solange != null, läuft ein Sub-World-Auth-Switch und der finally-Hook
  // in loadUserTeamData entlädt isSwitching erst dann.
  const pendingSubWorldSwitchRef = useRef(null);
  
  // Status
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  // 🔒 SICHERHEIT: Abort-Controller für Request-Cancellation bei World-Switch
  const createNewAbortController = useCallback(() => {
    // Alte Requests abbrechen
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current;
  }, []);

  /**
   * 🔒 SICHERHEIT: Holt das aktuelle AbortSignal für Requests
   * Verwende das in allen fetch/API Calls
   */
  const getAbortSignal = useCallback(() => {
    return abortControllerRef.current?.signal || null;
  }, []);

  /**
   * 🔒 SICHERHEIT: Validiert ob eine worldId der aktuellen entspricht
   * MUSS vor kritischen Aktionen (E-Mail senden, Daten ändern) geprüft werden!
   */
  const validateWorldId = useCallback((worldIdToCheck) => {
    if (!activeWorldId) return false;
    if (isSwitching) return false; // Während Switch IMMER false
    return worldIdToCheck === activeWorldId;
  }, [activeWorldId, isSwitching]);

  // Auth State Listener
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setCurrentUserId(user.uid);
        await loadUserTeamData(user.uid);
      } else {
        resetState();
      }
      setIsInitialized(true);
    });

    return () => unsubscribe();
  }, []);

  /**
   * Lädt die Team-Daten des Users (Multi-Team Support)
   */
  const loadUserTeamData = async (userId) => {
    setIsLoading(true);
    createNewAbortController();
    
    try {
      // Lade User-Daten aus Realtime Database
      const userRef = ref(database, `Users/${userId}`);
      const snapshot = await get(userRef);
      const userData = snapshot.exists() ? snapshot.val() : null;

      if (userData) {
        const type = userData.accountType || 'owner';
        setAccountType(type);
        
        // Sub-World Status (Session = Sub-World, wenn parentOwnerUid gesetzt)
        setParentOwnerUid(userData.parentOwnerUid || null);
        setMultiInstanceEnabled(userData.multiInstance === true);
        setMultiInstanceCap(resolveInstanceCap(userData));
        
        // Liste der eigenen Sub-Worlds (nur sichtbar im Parent-Account)
        const ownedSubWorldsRaw = userData.subWorlds || {};
        const ownedSubWorlds = Object.entries(ownedSubWorldsRaw)
          .filter(([, v]) => v && v.archived !== true)
          .map(([subId, v]) => ({
            subWorldId: subId,
            instanceName: v.instanceName || 'Sub-Instance',
            createdAt: v.createdAt || null,
          }));
        setMySubWorlds(ownedSubWorlds);
        
        // Sammle alle verfügbaren Teams
        const allTeams = [];
        const pendingInvites = [];
        
        // 1. Eigener Account - nur wenn User KEIN reiner Team-Member ist
        if (type !== 'member') {
          const isSubWorldSession = !!userData.parentOwnerUid;
          const ownTeam = {
            teamId: userId,
            teamName: isSubWorldSession
              ? (userData.instanceName || userData.name || 'Sub-Instance')
              : 'My Account',
            ownerEmail: userData.email,
            ownerName: userData.name || '',
            features: ['all'],
            memberType: null,
            readOnly: false,
            isOwnAccount: true,
            isSubWorldSession,
            parentOwnerUid: userData.parentOwnerUid || null,
          };
          allTeams.push(ownTeam);
        }
        
        // 2. Multi-Team Support - teamMemberships (plural)
        if (userData.teamMemberships && Object.keys(userData.teamMemberships).length > 0) {
          for (const [teamId, membership] of Object.entries(userData.teamMemberships)) {
            // Unterscheide zwischen akzeptierten und ausstehenden Einladungen
            if (membership.status === 'pending') {
              pendingInvites.push({
                teamId,
                teamName: membership.teamName || 'Team',
                ownerEmail: membership.ownerEmail || '',
                ownerName: membership.ownerName || '',
                features: membership.features || ['leads'],
                memberType: membership.memberType || 'standard',
                readOnly: membership.readOnly || false,
                invitedAt: membership.addedAt
              });
            } else if (membership.status === 'accepted' || !membership.status) {
              // status === 'accepted' ODER kein status (legacy) = akzeptiert
              allTeams.push({
                teamId,
                teamName: membership.teamName || 'Team',
                ownerEmail: membership.ownerEmail || '',
                ownerName: membership.ownerName || '',
                features: membership.features || ['leads'],
                memberType: membership.memberType || 'standard',
                readOnly: membership.readOnly || false,
                addedAt: membership.addedAt,
                isOwnAccount: false,
                // Sub-World-Memberships (vom Parent als master eingetragen) markieren,
                // damit TeamSelector sie als "My Instance" rendern kann statt als Team.
                isSubWorld: membership.isSubWorld === true,
              });
            }
          }
        }
        
        // 3. LEGACY: Einzelnes Team (alte Struktur) - nur wenn nicht in neuer Struktur
        if (userData.teamMembership && userData.teamMembership.teamId) {
          const legacyTeamId = userData.teamMembership.teamId;
          // Nur hinzufügen wenn nicht bereits in allTeams
          if (!allTeams.some(t => t.teamId === legacyTeamId)) {
            allTeams.push({
              teamId: legacyTeamId,
              teamName: userData.teamMembership.teamName || 'Team',
              ownerEmail: userData.teamMembership.ownerEmail || '',
              ownerName: userData.teamMembership.ownerName || '',
              features: userData.teamMembership.features || ['leads'],
              memberType: userData.teamMembership.memberType || 'standard',
              readOnly: userData.teamMembership.readOnly || false,
              isOwnAccount: false
            });
          }
        }
        
        // Speichere ausstehende Einladungen
        setPendingInvitations(pendingInvites);
        
        // Speichere alle verfügbaren Teams
        setAvailableTeams(allTeams);
        
        console.log('📋 Verfügbare Teams:', allTeams.map(t => ({ id: t.teamId, name: t.teamName, isOwn: t.isOwnAccount })));
        console.log('📨 Ausstehende Einladungen:', pendingInvites.length);
        
        // Wähle das aktive Team
        if (allTeams.length > 0) {
          // Prüfe localStorage für letztes aktives Team
          const lastActiveTeamId = localStorage.getItem(`bonusclub_lastTeam_${userId}`);
          let activeTeamData = allTeams.find(t => t.teamId === lastActiveTeamId);
          
          // Fallback: Erstes Team das KEIN eigener Account ist (bevorzuge Team-Mitgliedschaften)
          if (!activeTeamData) {
            activeTeamData = allTeams.find(t => !t.isOwnAccount) || allTeams[0];
          }
          
          console.log('🎯 Aktiviere Team:', activeTeamData.teamName, '(ID:', activeTeamData.teamId, ')');
          await activateTeam(activeTeamData, userId);
        } else {
          // Kein Team verfügbar (sollte nicht passieren, aber Fallback)
          console.warn('⚠️ Kein Team verfügbar, setze auf eigene ID');
          setActiveWorldId(userId);
          setActiveTeam(null);
          setTeamOwnerId(null);
          setTeamOwnerEmail(null);
          setTeamOwnerName(null);
          setFeatures(['all']);
          setReadOnly(false);
          setMemberType(null);
        }
      } else {
        // Neuer User ohne Daten
        setActiveWorldId(userId);
        setAccountType('pending');
        setAvailableTeams([]);
        setActiveTeam(null);
        setFeatures([]);
        setReadOnly(false);
        setParentOwnerUid(null);
        setMySubWorlds([]);
        setMultiInstanceEnabled(false);
        setMultiInstanceCap(0);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Team-Daten:', error);
      setActiveWorldId(userId);
      setAccountType('owner');
      setFeatures(['all']);
    } finally {
      setIsLoading(false);
      // Sub-World-Auth-Switch: jetzt erst die UI freigeben, weil die neue
      // UID-spezifische Datenladung abgeschlossen ist.
      if (pendingSubWorldSwitchRef.current) {
        pendingSubWorldSwitchRef.current = null;
        setIsSwitching(false);
      }
    }
  };

  /**
   * 🔒 SICHERHEIT: Aktiviert ein Team mit komplettem UI-Block
   */
  const activateTeam = async (team, userId) => {
    setTeamOwnerId(team.teamId);
    setActiveWorldId(team.teamId);
    setActiveTeam(team);
    setTeamOwnerEmail(team.ownerEmail);
    setTeamOwnerName(team.ownerName);
    setFeatures(team.features || ['leads']);
    setReadOnly(team.readOnly || false);
    setMemberType(team.memberType);
    
    // Speichere letztes aktives Team
    if (userId) {
      localStorage.setItem(`bonusclub_lastTeam_${userId}`, team.teamId);
    }
  };

  /**
   * 🔒 SICHERHEIT: Wechselt zu einem anderen Team
   * - Blockt UI komplett während des Wechsels
   * - Bricht alle pending Requests ab
   * - Inkrementiert switchVersion für Validation
   */
  const switchTeam = useCallback(async (teamId) => {
    const targetTeam = availableTeams.find(t => t.teamId === teamId);
    if (!targetTeam) {
      console.error('Team nicht gefunden:', teamId);
      return false;
    }
    
    if (activeWorldId === teamId) {
      return true; // Bereits aktiv
    }
    
    console.log('🔒 TEAM SWITCH: Starte sicheren Wechsel zu', targetTeam.teamName);
    
    // 🔒 SCHRITT 1: UI blocken
    setIsSwitching(true);
    
    // 🔒 SCHRITT 2: Alle pending Requests abbrechen
    createNewAbortController();
    
    // 🔒 SCHRITT 3: Switch-Version inkrementieren (invalidiert alte Requests)
    setSwitchVersion(v => v + 1);
    
    // Kurze Verzögerung um sicherzustellen dass alle States gecleared sind
    await new Promise(resolve => setTimeout(resolve, 100));
    
    try {
      // 🔒 SCHRITT 4: Neues Team aktivieren
      await activateTeam(targetTeam, currentUserId);
      
      console.log('✅ TEAM SWITCH: Erfolgreich gewechselt zu', targetTeam.teamName);
      
      await new Promise(resolve => setTimeout(resolve, 400));
      
      return true;
    } catch (error) {
      console.error('❌ TEAM SWITCH: Fehler beim Wechsel:', error);
      return false;
    } finally {
      // 🔒 SCHRITT 5: UI wieder freigeben
      setIsSwitching(false);
    }
  }, [availableTeams, activeWorldId, currentUserId, createNewAbortController]);

  /**
   * State zurücksetzen (bei Logout)
   */
  const resetState = () => {
    // Alle Requests abbrechen
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    setCurrentUserId(null);
    setActiveWorldId(null);
    setAccountType(null);
    setMemberType(null);
    setAvailableTeams([]);
    setActiveTeam(null);
    setPendingInvitations([]);
    setTeamOwnerId(null);
    setTeamOwnerEmail(null);
    setTeamOwnerName(null);
    setFeatures([]);
    setReadOnly(false);
    setIsSwitching(false);
    setSwitchVersion(0);
    setIsLoading(false);
    setParentOwnerUid(null);
    setMySubWorlds([]);
    setMultiInstanceEnabled(false);
    setMultiInstanceCap(0);
    pendingSubWorldSwitchRef.current = null;
  };

  // ============ HELPER FUNCTIONS ============

  const hasFeature = (feature) => {
    if (isSwitching) return false; // 🔒 Während Switch keine Features
    if (features.includes('all')) return true;
    return features.includes(feature);
  };

  const canWrite = () => {
    if (isSwitching) return false; // 🔒 Während Switch kein Schreiben
    return !readOnly;
  };

  const canAccessSettings = () => {
    if (isSwitching) return false;
    return hasFeature('settings') || hasFeature('all');
  };

  const canManageTeam = () => {
    if (isSwitching) return false;
    return accountType === 'owner';
  };

  /**
   * Einladung akzeptieren
   * 🔧 FIX: Aktualisiert sowohl Realtime Database ALS AUCH Firestore!
   * Die Firestore Security Rules prüfen teams/{ownerId}/members - daher muss auch dort aktualisiert werden.
   */
  const acceptInvitation = useCallback(async (teamId) => {
    if (!currentUserId || !teamId) return false;
    
    try {
      // 1. Hole die Einladungsdaten
      const invitation = pendingInvitations.find(inv => inv.teamId === teamId);
      const membershipRef = ref(database, `Users/${currentUserId}/teamMemberships/${teamId}`);
      const membershipSnapshot = await get(membershipRef);
      const membershipData = membershipSnapshot.exists() ? membershipSnapshot.val() : invitation;
      
      // 2. Realtime Database aktualisieren
      await update(membershipRef, {
        status: 'accepted',
        acceptedAt: new Date().toISOString()
      });
      
      // 3. 🔧 FIX: Firestore Team-Dokument auch aktualisieren!
      // Die Security Rules prüfen teams/{ownerId}/members - daher muss der Status dort auch 'accepted' sein
      try {
        const teamDocRef = doc(firestore, 'teams', teamId);
        
        // Hole aktuellen User-Email
        const currentUserEmail = auth.currentUser?.email || '';
        
        // Update das Member-Dokument in Firestore
        await setDoc(teamDocRef, {
          members: {
            [currentUserId]: {
              email: currentUserEmail,
              role: 'member',
              memberType: membershipData?.memberType || 'standard',
              features: membershipData?.features || ['leads'],
              readOnly: membershipData?.readOnly || false,
              status: 'accepted',
              acceptedAt: serverTimestamp()
            }
          }
        }, { merge: true });
        
        console.log('✅ Firestore Team-Dokument aktualisiert für Member:', currentUserId);
      } catch (firestoreError) {
        // Firestore Update ist optional - falls es fehlschlägt, loggen aber nicht abbrechen
        console.warn('⚠️ Firestore Team-Dokument konnte nicht aktualisiert werden (wird ggf. später synchronisiert):', firestoreError);
      }
      
      // 4. Team aus pendingInvitations entfernen und zu availableTeams hinzufügen
      if (invitation) {
        setPendingInvitations(prev => prev.filter(inv => inv.teamId !== teamId));
        
        const newTeam = {
          ...invitation,
          memberType: invitation.memberType || 'standard',
          readOnly: invitation.readOnly || false,
          isOwnAccount: false
        };
        setAvailableTeams(prev => [...prev, newTeam]);
        
        // FIX: Immer das neu akzeptierte Team aktivieren und dahin wechseln
        // Der User hat gerade eine Einladung akzeptiert, also will er dieses Team sehen
        console.log('🔄 Aktiviere neu akzeptiertes Team:', newTeam.teamName);
        await activateTeam(newTeam, currentUserId);
      }
      
      console.log('✅ Einladung akzeptiert für Team:', teamId);
      return true;
    } catch (error) {
      console.error('❌ Fehler beim Akzeptieren der Einladung:', error);
      return false;
    }
  }, [currentUserId, pendingInvitations]);

  /**
   * Team verlassen (Self-Leave).
   *
   * Der User entfernt sich SELBST aus einem Team, in das er von einem anderen
   * Owner hinzugefügt wurde. Läuft über die Cloud Function `leaveTeam`, weil
   * Firestore-Rules Team-Members keinen direkten Write auf das Team-Dokument
   * erlauben (nur der Owner darf das mutieren).
   *
   * Reihenfolge ist kritisch:
   *  1. Wenn das zu verlassende Team gerade AKTIV ist, MÜSSEN wir vorher zum
   *     eigenen Account (oder einem anderen Team) wechseln. Andernfalls würden
   *     alle laufenden Firestore-Listener (Inbox, Leads, Settings...) auf
   *     einmal permission-denied werfen, sobald die Function den Member-Eintrag
   *     gelöscht hat. Der Team-Switch bricht diese Requests sauber ab.
   *  2. Danach Cloud Function aufrufen.
   *  3. Zum Schluss availableTeams neu laden, damit die alte Karte sofort
   *     aus dem TeamSelector verschwindet.
   */
  const leaveTeamFn = useCallback(async (teamOwnerId) => {
    if (!currentUserId || !teamOwnerId) return false;
    if (teamOwnerId === currentUserId) {
      console.warn('[leaveTeam] Cannot leave own account');
      return false;
    }

    try {
      // 1. Wenn wir gerade IN dem Team sind, das wir verlassen: erst weg
      //    switchen. Priorisiert eigenes Account – das ist immer da und
      //    hat immer volle Rechte, egal was passiert.
      if (activeWorldId === teamOwnerId) {
        const fallbackTeam =
          availableTeams.find(t => t.teamId === currentUserId) ||
          availableTeams.find(t => t.teamId !== teamOwnerId);
        if (fallbackTeam) {
          await switchTeam(fallbackTeam.teamId);
        }
      }

      // 2. Cloud Function aufrufen (Admin-Rechte für den Team-Doc-Write).
      const fn = httpsCallable(functions, 'leaveTeam');
      await fn({ teamOwnerId });

      // 3. Lokalen State sofort bereinigen. Kein Warten auf den nächsten
      //    Load – der User soll die verlassene Karte sofort weg sehen.
      setAvailableTeams(prev => prev.filter(t => t.teamId !== teamOwnerId));

      // 4. Kanonisches Reload aus der Datenbank – falls Firestore/RTDB
      //    zwischenzeitlich noch andere Änderungen hatten, sind wir wieder
      //    synchron.
      await loadUserTeamData(currentUserId);

      console.log('✅ Team verlassen:', teamOwnerId);
      return true;
    } catch (err) {
      console.error('❌ Fehler beim Verlassen des Teams:', err);
      return false;
    }
  }, [currentUserId, activeWorldId, availableTeams, switchTeam]);

  /**
   * Einladung ablehnen
   */
  const declineInvitation = useCallback(async (teamId) => {
    if (!currentUserId || !teamId) return false;
    
    try {
      const membershipRef = ref(database, `Users/${currentUserId}/teamMemberships/${teamId}`);
      await remove(membershipRef);
      
      // Aus pendingInvitations entfernen
      setPendingInvitations(prev => prev.filter(inv => inv.teamId !== teamId));
      
      console.log('✅ Einladung abgelehnt für Team:', teamId);
      return true;
    } catch (error) {
      console.error('❌ Fehler beim Ablehnen der Einladung:', error);
      return false;
    }
  }, [currentUserId]);

  // ============ SUB-WORLD ACTIONS ============
  // Alle Mutations laden die Team-Daten danach neu, damit availableTeams konsistent bleiben.
  
  /**
   * Wechselt per Custom-Token-Login in eine Sub-World hinein.
   * Die UI bleibt während des Wechsels gesperrt (isSwitching), bis
   * loadUserTeamData für die neue UID abgeschlossen ist.
   */
  const enterSubWorld = useCallback(async (subWorldId) => {
    if (!subWorldId) return false;
    try {
      setIsSwitching(true);
      pendingSubWorldSwitchRef.current = subWorldId;
      createNewAbortController();
      setSwitchVersion(v => v + 1);
      
      const fn = httpsCallable(functions, 'signInToSubWorld');
      const result = await fn({ subWorldId });
      const customToken = result?.data?.customToken;
      if (!customToken) throw new Error('No custom token returned');
      
      // Wichtig: Parent's localStorage NICHT löschen — wir wollen, dass der Parent
      // bei Rückkehr (returnToParentAccount) auf seinem zuletzt aktiven Team landet.
      // localStorage-Keys sind pro UID gescoped, kein Konflikt mit der Sub-World-Session.
      
      await signInWithCustomToken(auth, customToken);
      // onAuthStateChanged → loadUserTeamData(subWorldId) → finally clears isSwitching.
      return true;
    } catch (err) {
      console.error('[enterSubWorld] failed:', err);
      pendingSubWorldSwitchRef.current = null;
      setIsSwitching(false);
      return false;
    }
  }, [createNewAbortController]);
  
  /**
   * Verlässt eine Sub-World-Session und kehrt in den Parent-Account zurück.
   * Erfordert serverseitig den Custom Claim `subWorld:true` (gesetzt beim Eintritt).
   */
  const returnToParentAccountFn = useCallback(async () => {
    if (!parentOwnerUid) return false;
    try {
      setIsSwitching(true);
      pendingSubWorldSwitchRef.current = parentOwnerUid;
      createNewAbortController();
      setSwitchVersion(v => v + 1);
      
      const fn = httpsCallable(functions, 'returnToParentAccount');
      const result = await fn({});
      const customToken = result?.data?.customToken;
      if (!customToken) throw new Error('No custom token returned');
      
      // Sub-World-localStorage zurücklassen, der Parent hat seinen eigenen Key.
      
      await signInWithCustomToken(auth, customToken);
      return true;
    } catch (err) {
      console.error('[returnToParentAccount] failed:', err);
      pendingSubWorldSwitchRef.current = null;
      setIsSwitching(false);
      return false;
    }
  }, [parentOwnerUid, createNewAbortController]);
  
  /**
   * Erstellt eine neue Sub-World. Lädt anschließend die Team-Daten neu, damit
   * die neue Instanz sofort im TeamSelector erscheint.
   */
  const createSubWorldFn = useCallback(async (instanceName) => {
    if (!currentUserId) return null;
    try {
      const fn = httpsCallable(functions, 'createSubWorld');
      const result = await fn({ instanceName });
      const subWorldId = result?.data?.subWorldId;
      if (!subWorldId) return null;
      await loadUserTeamData(currentUserId);
      return { subWorldId, instanceName: result.data.instanceName };
    } catch (err) {
      console.error('[createSubWorld] failed:', err);
      throw err;
    }
  }, [currentUserId]);
  
  const renameSubWorldFn = useCallback(async (subWorldId, instanceName) => {
    if (!currentUserId || !subWorldId) return false;
    try {
      const fn = httpsCallable(functions, 'renameSubWorld');
      await fn({ subWorldId, instanceName });
      await loadUserTeamData(currentUserId);
      return true;
    } catch (err) {
      console.error('[renameSubWorld] failed:', err);
      throw err;
    }
  }, [currentUserId]);
  
  const archiveSubWorldFn = useCallback(async (subWorldId) => {
    if (!currentUserId || !subWorldId) return false;
    try {
      const fn = httpsCallable(functions, 'archiveSubWorld');
      await fn({ subWorldId });
      await loadUserTeamData(currentUserId);
      return true;
    } catch (err) {
      console.error('[archiveSubWorld] failed:', err);
      throw err;
    }
  }, [currentUserId]);
  
  const transferSubWorldFn = useCallback(async (subWorldId, newOwnerEmail) => {
    if (!currentUserId || !subWorldId || !newOwnerEmail) return false;
    try {
      const fn = httpsCallable(functions, 'transferSubWorld');
      await fn({ subWorldId, newOwnerEmail });
      await loadUserTeamData(currentUserId);
      return true;
    } catch (err) {
      console.error('[transferSubWorld] failed:', err);
      throw err;
    }
  }, [currentUserId]);
  
  const isOwner = accountType === 'owner';
  const isMember = accountType === 'member';
  const isMasterMember = accountType === 'member' && (memberType === 'master' || features.includes('all'));
  const isStandardMember = accountType === 'member' && !isMasterMember;
  const hasMultipleTeams = availableTeams.length > 1;

  // Context Value
  const contextValue = {
    // User IDs
    currentUserId,
    activeWorldId,
    
    // Account-Typ
    accountType,
    memberType,
    isOwner,
    isMember,
    isMasterMember,
    isStandardMember,
    
    // Multi-Team Support
    availableTeams,
    activeTeam,
    hasMultipleTeams,
    pendingInvitations,
    switchTeam,
    acceptInvitation,
    declineInvitation,
    leaveTeam: leaveTeamFn,
    
    // Team-Infos
    teamOwnerId,
    teamOwnerEmail,
    teamOwnerName,
    
    // Berechtigungen
    features,
    readOnly,
    
    // 🔒 SICHERHEIT
    isSwitching,
    switchVersion,
    validateWorldId,
    getAbortSignal,
    
    // Status
    isLoading,
    isInitialized,
    
    // Sub-World System
    parentOwnerUid,
    isSubWorldSession: !!parentOwnerUid,
    mySubWorlds,
    multiInstanceEnabled,
    multiInstanceCap,
    
    // Helper Functions
    hasFeature,
    canWrite,
    canAccessSettings,
    canManageTeam,
    
    // Sub-World Actions
    enterSubWorld,
    returnToParentAccount: returnToParentAccountFn,
    createSubWorld: createSubWorldFn,
    renameSubWorld: renameSubWorldFn,
    archiveSubWorld: archiveSubWorldFn,
    transferSubWorld: transferSubWorldFn,
    
    // Reload Function
    reloadTeamData: () => currentUserId && loadUserTeamData(currentUserId),
  };

  return (
    <TeamContext.Provider value={contextValue}>
      {children}
    </TeamContext.Provider>
  );
};

/**
 * Custom Hook für einfachen Zugriff auf TeamContext
 */
export const useTeam = () => {
  const context = useContext(TeamContext);
  if (!context) {
    throw new Error('useTeam must be used within a TeamProvider');
  }
  return context;
};

export default TeamContext;
